// src/pages/customerApp/News.tsx
import React, { useEffect, useState } from 'react';
import { Card, Typography, Empty, Row, Col, Button, Space, Spin, Image } from 'antd';
import {
  ArrowLeftOutlined,
  FireOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './customerHome.css';
import { listNews, type NewsDTO as NewsBase } from '../../services/news';
import { tokenManager } from '../../context/AuthContext';

const { Title, Text, Paragraph } = Typography;

type NewsDTO = NewsBase & {
  createdAt?: string | null;
  imageUrl?: string | null;
  author?: string | null;
  content?: string | null;
};


const timeAgoIT = (iso?: string | null) => {
  if (!iso) return '-';
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const m = 60 * 1000,
    h = 60 * m,
    d = 24 * h;
  if (diffMs < h) {
    const mins = Math.max(1, Math.floor(diffMs / m));
    return `${mins}m fa`;
  }
  if (diffMs < d) return `${Math.floor(diffMs / h)}h fa`;
  const days = Math.floor(diffMs / d);
  if (days === 1) return 'Ieri';
  if (days < 7) return `${days}g fa`;
  return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
};

const PREVIEW_MAX_CHARS = 120;

function truncateByChars(text?: string | null, max = PREVIEW_MAX_CHARS): string {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  // taglio sullo spazio piu vicino per evitare di spezzare la parola
  const slice = trimmed.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  const base = lastSpace > 0 ? slice.slice(0, lastSpace) : slice;
  return base.replace(/[.,;:!?\s]*$/, '') + '...';
}


const AuthImage: React.FC<{
  src?: string | null;
  alt?: string;
  style?: React.CSSProperties;
  className?: string;
  preview?: boolean;
  height?: number | string;
}> = ({ src, alt, style, className, preview = false, height }) => {
  const [blobUrl, setBlobUrl] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    let revoked: string | null = null;
    const abort = new AbortController();

    const load = async () => {
      if (!src) {
        setBlobUrl(undefined);
        return;
      }
      // Se non è un path /api/p/... (pubblico/CDN), non serve bearer
      if (!src.startsWith('/api/')) {
        setBlobUrl(src);
        return;
      }
      try {
        setLoading(true);
        const token = tokenManager.getAccess?.();
        const res = await fetch(src, {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: abort.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        revoked = url;
      } catch {
        setBlobUrl(undefined);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => {
      abort.abort();
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [src]);

  // Skeleton semplice
  if (!blobUrl && loading) {
    return (
      <div
        className={className}
        style={{
          height: typeof height !== 'undefined' ? height : 260,
          background: '#f0f2f5',
          ...style,
        }}
      />
    );
  }

  if (!blobUrl) return null;

  return (
    <Image
      alt={alt}
      src={blobUrl}
      preview={preview}
      className={className}
      style={style}
    />
  );
};

const News: React.FC = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsDTO[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const p = await listNews();
        const content: NewsDTO[] = (Array.isArray(p)
          ? p
          : p?.content ?? []) as NewsDTO[];
        const sorted = [...content].sort(
          (a, b) =>
            new Date(b.createdAt ?? 0).getTime() -
            new Date(a.createdAt ?? 0).getTime()
        );
        setNews(sorted);
      } catch (e) {
        setNews([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="customer-home-v2 page-transition" style={{ paddingTop: 16 }}>
      <div className="container-v2">
        {/* Header style identico */}
        <div className="header-v2" style={{ marginBottom: 24 }}>
          <div className="header-content-v2">
            <div className="user-info">
              <div className="user-details">
              
                <Title level={1} className="user-name">
                  News & Aggiornamenti
                </Title>
              </div>
            </div>
            <Button
              type="default"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              className="logout-button-v2"
            >
              Indietro
            </Button>
          </div>
        </div>

        {/* Lista: tutte card grandi (ognuna con AuthImage) */}
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : news.length === 0 ? (
          <Card className="empty-card" bordered={false}>
            <Empty description="Nessuna news disponibile" />
          </Card>
        ) : (
          <Row gutter={[24, 24]}>
            {news.map((item, idx) => (
              <Col xs={24} key={item.id}>
                <Card
                  className="featured-news-card"
                  bordered={false}
                  onClick={() => navigate(`/news/${item.id}`)}
                  bodyStyle={{ padding: 12 }}
                >
                 

                  <Row gutter={[32, 32]} align="middle">
                    <Col xs={24} lg={14}>
                      <div
                        style={{
                          height: 260,
                          overflow: 'hidden',
                          borderRadius: 20,
                        }}
                      >
                        <AuthImage
                          alt={item.title}
                          src={item.imageUrl}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          height={260}
                          preview={false}
                        />
                      </div>
                    </Col>
                    <Col xs={24} lg={10}>
                      <div className="featured-content">
                        <Title level={3} className="featured-title">
                          {item.title}
                        </Title>
                        {item.content && (
                          <Paragraph className="featured-excerpt">
                            {truncateByChars(item.content, 200)}

                          </Paragraph>
                        )}

                        <div className="featured-meta">
                          <Space size={20}>
                            <Space size={6}>
                              <UserOutlined className="meta-icon" />
                             <Text className="meta-text">
                                {item.author}
                              </Text>
                            </Space>
                            <Space size={6}>
                              <ClockCircleOutlined className="meta-icon" />
                              <Text className="meta-text">
                                {timeAgoIT(item.createdAt)}
                              </Text>
                            </Space>
                          </Space>
                        </div>

                        <Button
                          type="primary"
                          size="large"
                          className="read-more-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/news/${item.id}`);
                          }}
                        >
                          Leggi l'articolo
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>
    </div>
  );
};

export default News;
